/**
 * Renders an epoch-millis timestamp as a short relative string like
 * "just now", "5 min ago", "2 h ago", "3 d ago". Falls back to a locale
 * date string for anything older than a week.
 *
 * Kept standalone (no deps) so fixture/test usage is trivial and so the
 * message bubble doesn't drag in Intl.RelativeTimeFormat (which works but
 * is heavy for the 4-5 ranges we care about).
 */
export function relativeTime(ts: number, now: number = Date.now()): string {
  const delta = now - ts;
  if (Number.isNaN(delta)) {
    return '';
  }
  if (delta < 0) {
    // Future timestamps are an input oddity — render as "just now" so
    // the UI never shows a nonsense negative value.
    return 'just now';
  }
  const sec = Math.floor(delta / 1000);
  if (sec < 60) {
    return 'just now';
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min} min ago`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr} h ago`;
  }
  const day = Math.floor(hr / 24);
  if (day < 7) {
    return `${day} d ago`;
  }
  return new Date(ts).toLocaleDateString();
}
